import React, { Fragment, useRef, useEffect, useCallback } from "react";
import Modal from "components/Modal";
import ReactDOM from "react-dom";
import {
  CommandContextProvider,
  useCommandContext
} from "utils/context/command";
import { useOnClickOutside } from "utils/hooks/ui";
import scrollIntoView from "smooth-scroll-into-view-if-needed";
import Trigger from "./Trigger";
import QueryInput from "./QueryInput";
import {
  BORDER_STYLE,
  MAX_HEIGHT,
  PADDER_HEIGHT,
  COMMAND_TYPE
} from "./constants";
import Item from "./Item";
import Suggestion from "./Suggestion";
import HoverIconButton from "components/HoverIconButton";
import { getJWT } from "utils/auth";
import LoadingSpinner from "components/LoadingSpinner";
import CommandVideoEmbed from "./CommandVideoEmbed";
import { getCommandModalClasses } from "utils/command";

const COMMAND_ITEM = "command-item";

const getItemId = (index) => `${COMMAND_ITEM}-${index}`;

const CommandPaletteContent = () => {
  const ref = useRef();

  const {
    values: { query, index, debug },
    loading,
    activeId,
    sections,
    suggestions,
    actions: { addHistory, popHistory, reset },
    activeCommand
  } = useCommandContext();

  /**
   * Close on click outside
   */
  const hideMenu = useCallback(() => reset(), []);
  useOnClickOutside(ref, hideMenu);

  /**
   * Scroll command item into view on index change
   * - i.e. on keyboard up | down we update index
   */
  useEffect(() => {
    const activeItem = document.getElementById(getItemId(index));

    if (activeItem) {
      scrollIntoView(activeItem, {
        behavior: "smooth",
        scrollMode: "always",
        block: "center"
      });
    }
  }, [index]);

  let indexCounter = 0;
  let nodes = [
    <div
      key="padder"
      id={getItemId(indexCounter)}
      tabIndex={0}
      style={{ height: PADDER_HEIGHT, backgroundColor: "transparent" }}
      className="w-100"
    ></div>
  ];

  for (const section in sections) {
    const hasSectionName = section && section !== "undefined";
    if (hasSectionName) {
      const sectionIdKey = [section];
      if (activeId) {
        sectionIdKey.push(activeId);
      }
      nodes.push(
        <div
          key={sectionIdKey.join("-")}
          className="f7 ttu pv2 ph3 fw6"
          style={BORDER_STYLE}
        >
          {section}
        </div>
      );
    }
    const items = sections[section];
    if (Array.isArray(items)) {
      items.forEach((item) => {
        indexCounter++;

        nodes.push(
          <Item
            key={item.id}
            item={item}
            index={indexCounter}
            addHistory={addHistory}
            reset={reset}
          />
        );
      });
    }
  }

  if (Array.isArray(suggestions) && suggestions.length) {
    nodes.push(
      <div
        key="suggestions"
        className="f7 ttu pv2 ph3 fw6"
        style={BORDER_STYLE}
      >
        Suggestions
      </div>
    );
    suggestions.forEach((item) => {
      indexCounter++;

      nodes.push(
        <Suggestion
          key={item.id}
          item={item}
          index={indexCounter}
          addHistory={addHistory}
          reset={reset}
        />
      );
    });
  }
  const isVideo = activeCommand && activeCommand.type === COMMAND_TYPE.VIDEO;

  const content = isVideo ? (
    <CommandVideoEmbed {...activeCommand.config}>
      <div className="absolute left-0 top-0 h-100 flex items-center">
        <HoverIconButton size={22} icon="back" onClick={popHistory} />
      </div>
    </CommandVideoEmbed>
  ) : (
    <Fragment>
      {debug && <div className="f7 z-1 absolute top-0 left-0">{query}</div>}
      {loading && (
        <div
          style={{ top: 18 }}
          className="absolute top-0 right-1 bottom-0 z-1"
        >
          <LoadingSpinner customClasses={{ ring: "blue", size: "small" }} />
        </div>
      )}
      <QueryInput />
      <div
        style={{ maxHeight: MAX_HEIGHT }}
        className="w-100 overflow-y-scroll"
      >
        {nodes}
      </div>
    </Fragment>
  );

  return (
    <div ref={ref} className="w-100 overflow-hidden relative">
      {content}
    </div>
  );
};

const CommandPaletteContainer = ({ trigger }) => {
  const {
    values: { open },
    activeCommand,
    actions: { setOpen }
  } = useCommandContext();

  const isVideo = activeCommand && activeCommand.type === COMMAND_TYPE.VIDEO;

  let triggerNode = null;
  const triggerProps = { onClick: () => setOpen(true) };
  if (trigger) {
    /**
     * Apply minimum onClick handler
     */
    triggerNode = !trigger.props.onClick
      ? React.cloneElement(trigger, triggerProps)
      : trigger;
  } else {
    triggerNode = <Trigger {...triggerProps} />;
  }

  return (
    <Fragment>
      {triggerNode}
      {open
        ? ReactDOM.createPortal(
            <Modal
              showModal={open}
              showCloseIcon={false}
              onClose={() => setOpen(false)}
              customClasses={getCommandModalClasses({ isVideo })}
            >
              <CommandPaletteContent />
            </Modal>,
            document.body
          )
        : null}
    </Fragment>
  );
};

const CommandPalette = ({
  commands,
  id,
  actions,
  initialValues,
  trigger,
  preventKeyboardTrigger
}) => {
  const providerProps = {
    actions,
    commands,
    initialValues,
    preventKeyboardTrigger,
    apiKey: getJWT()
  };
  if (id) {
    providerProps.id = id;
  }

  return (
    <CommandContextProvider {...providerProps}>
      <CommandPaletteContainer trigger={trigger} />
    </CommandContextProvider>
  );
};

export default CommandPalette;
